import React, { memo, useContext, useEffect, useMemo, useRef, useState } from "react";

import { CoreLayoutV2, isAndroidPlatform } from "@swiggy-private/core-ui";

import { ToingHeader } from "@cart-client/components/toing-header";
import { CartContext } from "@cart-client/contexts/CartContext";
import { isToingRtbXp } from "@cart-client/helpers/ToingHeader";
import { CommonUIWrapperProps } from "@cart-client/interfaces/Cart";

import { COMMON_HEADER_TEST_IDS } from "@swiggy-private/cart-test-ids";
import {
    getBridgeSdk,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    allowInsetsForAndroid,
} from "@swiggy-private/checkout-core";
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";
import { updateStatusBarCleanup } from "@cart-client/helpers/IncognitoCartHelper";

const CONTENT_EXTRA_PADDING_BOTTOM = 60;
const RTB_CONTENT_MARGIN_TOP = -24;
const RTB_SCROLLED_HEADER_BOX_SHADOW =
    "0 8px var(--blur-med, 16px) 0 var(--container-border-tertiary, rgba(2, 6, 12, 0.10))";

interface ToingUIWrapperProps extends CommonUIWrapperProps {
    restaurantName?: string;
    isCartError?: boolean;
    isCartLoading?: boolean;
    isEmptyCart?: boolean;
    enableAnimatedSavingsStrip?: boolean;
    openHalfCard?: VoidFunction;
    isFreeCashApplicable?: boolean;
    uploadId?: string;
}

const ToingUIWrapper = React.forwardRef<HTMLDivElement, ToingUIWrapperProps>(
    (props: ToingUIWrapperProps, ref): React.ReactElement => {
        const theme = useTheme();
        const headerRef = useRef<HTMLDivElement>(null);
        const { cartState } = useContext(CartContext);
        const isRtbXpEnabled = useMemo(() => isToingRtbXp(cartState?.reviewedCart ?? null), [cartState?.reviewedCart]);
        const [isRtbHeaderAnimationPlaying, setIsRtbHeaderAnimationPlaying] = useState(false);

        const containerBackgroundColor = useMemo(() => {
            const colors = theme.colors[theme.colorMode];

            if (props.isCartError || props.isEmptyCart) {
                return colors.background_Primary;
            }
            if (!isRtbXpEnabled && props.restaurantName) {
                return colors.primary;
            }
            // eslint-disable-next-line @typescript-eslint/naming-convention
            return colors.background_Secondary;
        }, [theme, props.isCartError, props.isEmptyCart, props.restaurantName, isRtbXpEnabled]);

        const headerContainerStyleProps = useMemo(() => {
            const isRtbScrolled = isRtbXpEnabled && Boolean(props.isPageScrolled);
            const applyRtbScrolledHeaderSurface = isRtbScrolled && !isRtbHeaderAnimationPlaying;
            return {
                background: applyRtbScrolledHeaderSurface
                    ? theme.colors[theme.colorMode].background_Primary
                    : "transparent",
                boxShadow: applyRtbScrolledHeaderSurface ? RTB_SCROLLED_HEADER_BOX_SHADOW : "none",
                zIndex: isRtbScrolled ? 9 : 0,
            };
        }, [isRtbXpEnabled, props.isPageScrolled, isRtbHeaderAnimationPlaying, theme]);

        useEffect(() => {
            const platform = (GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "").toLowerCase();
            const shouldChangeStatusBar = !isAndroidPlatform(platform) || allowInsetsForAndroid();

            if (shouldChangeStatusBar) {
                getBridgeSdk()
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    .changeStatusBarColor({
                        style: theme.colors[theme.colorMode].special_primary,
                        statusBarMode: isRtbXpEnabled ? "default" : "lightContent",
                        bottomBarMode: "default",
                    })
                    .catch(() => void 0);
            }

            return () => {
                updateStatusBarCleanup();
            };
        }, []);

        return (
            <CoreLayoutV2
                containerStyleProps={{
                    backgroundColor: containerBackgroundColor,
                }}
            >
                <>
                    <CoreLayoutV2.Header
                        dataTestId={COMMON_HEADER_TEST_IDS.CART_HEADER_PARENT}
                        containerRef={headerRef}
                        containerStyleProps={headerContainerStyleProps}
                    >
                        <ToingHeader
                            onBack={props.onBack}
                            restaurantName={props.restaurantName}
                            isCartError={props.isCartError}
                            isCartLoading={props.isCartLoading}
                            isEmptyCart={!!props.isEmptyCart}
                            isPageScrolled={props.isPageScrolled}
                            showAddressWidgetTop={props.showAddressWidgetTop}
                            openHalfCard={props.openHalfCard}
                            isFreeCashApplicable={props.isFreeCashApplicable}
                            uploadId={props.uploadId}
                            onRtbHeaderAnimationPlayingChange={setIsRtbHeaderAnimationPlaying}
                        >
                            {props.header}
                        </ToingHeader>
                    </CoreLayoutV2.Header>
                    <CoreLayoutV2.Content
                        containerStyleProps={{
                            backgroundColor:
                                props.isCartError || props.isEmptyCart
                                    ? "transparent"
                                    : // eslint-disable-next-line @typescript-eslint/naming-convention
                                      theme.colors[theme.colorMode].background_Secondary,
                            extraBottomPadding:
                                props.isEmptyCart || props.isCartError ? 0 : CONTENT_EXTRA_PADDING_BOTTOM,
                            ...(isRtbXpEnabled ? { marginTop: RTB_CONTENT_MARGIN_TOP } : {}),
                        }}
                        ref={ref}
                        // This is used to get the height of the header and adjust content height accordingly
                        headerRef={headerRef}
                        contentId={props.containerId}
                    >
                        {props.content}
                    </CoreLayoutV2.Content>
                </>
            </CoreLayoutV2>
        );
    },
);

if (__DEV__) ToingUIWrapper.displayName = "ToingUIWrapper";

export default memo(ToingUIWrapper);
